import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import { useDispatch } from 'react-redux';
import { CircularProgress } from '@mui/material';
import MainButton from '../../../buttons/MainButton';
import { appNotificationsTypes, responsiveSizeTimesheet } from '../../../../../utils/constants';
import { useWindowSize } from '../../../../../utils/hooks';
import SelectWithSearchInDropdown from '../../../SelectWithSearchInDropdown/SelectWithSearchInDropdown';
import { emailRegExp } from '../../../../../utils/regexps';
import colors from '../../../../../scss/colors';
import { tneAxiosClient } from '../../../../../api/apiSettings';
import { setNotification } from '../../../../../actions/appNotifications';
import classes from './SubmitModal.module.scss';
import { PageContext } from '../../../../../context/context';
import { generalEnums } from '../../../../../utils/enums';
import { buttonVariants } from '../../../buttons/types';

const textFieldStyles = (isDesktop) => ({
  width: isDesktop ? '342px' : '300px',
  backgroundColor: 'white',
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${colors.LIGHT_GREY}`,
    borderRadius: '10px'
  },
  color: colors.BLACK_TXT,
  fontSize: isDesktop ? '16px' : '14px',
  fontWeight: '325',
  lineHeight: '19px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
});

const ChooseApprover = ({ submit, changeStep, approvers, submitting }) => {
  const { t } = useTranslation();
  const size = useWindowSize();
  const [approver, setApprover] = useState(null);

  return (
    <>
      <p className={classes.mainLabel}>{t('dashboard.timesheet.submitwindow.confirmlabel')}</p>
      <SelectWithSearchInDropdown
        list={approvers || []}
        placeholder={t('dashboard.timesheet.submitwindow.chooseapprover')}
        placeholderSearch={t('dashboard.timesheet.submitwindow.approverplaceholder')}
        value={approver}
        setValue={setApprover}
        styles={{ marginTop: '34px', marginBottom: '34px' }}
      />
      <p role="presentation" className={classes.newApproverLink} onClick={changeStep}>
        {t('dashboard.timesheet.submitwindow.requestnew')}
      </p>
      <MainButton
        disabled={!approver || Array.isArray(approver)}
        variant={!approver || Array.isArray(approver) ? 'disabled' : 'primary'}
        rounded
        forceWidth={size.width > responsiveSizeTimesheet ? '242px' : '100%'}
        action={() => submit(approver.value)}
      >
        {!submitting ? t('common.general.submit') : <CircularProgress size={20} sx={{ color: colors.WHITE }} />}
      </MainButton>
    </>
  );
};

export const RequestNewApprover = ({ changeStep, Id }) => {
  const { t } = useTranslation();
  const size = useWindowSize();
  const dispatch = useDispatch();
  const ctx = useContext(PageContext);

  const [pending, setPending] = useState(false);
  const [data, setData] = useState({
    firstName: '',
    surname: '',
    email: ''
  });

  const requestNewApprover = async () => {
    setPending(true);
    try {
      const { TIMESHEET, EXPENSE } = generalEnums;
      await tneAxiosClient.post('/v1/request-approver', {
        FirstName: data.firstName,
        LastName: data.surname,
        PrimaryEmail: data.email,
        Type: ctx.pageType === TIMESHEET ? TIMESHEET : EXPENSE,
        TypeId: Id
      });
      changeStep();
    } catch (e) {
      dispatch(
        setNotification({
          type: appNotificationsTypes.ERROR,
          message: e?.response?.data || e.message,
          duration: 3000
        })
      );
    }
    setPending(false);
  };

  return (
    <>
      <p className={classes.mainLabel}>{t('dashboard.timesheet.submitwindow.requestnewlabel')}</p>
      <p className={classes.secondaryLabel}>
        {ctx.pageType === generalEnums.TIMESHEET
          ? t('dashboard.timesheet.submitwindow.requestnewtext')
          : t('dashboard.expenses.submitwindow.requestnewtext')}
      </p>
      <div className={classes.fieldsContainer}>
        <TextField
          value={data.firstName}
          onChange={(e) => setData((prev) => ({ ...prev, firstName: e.target.value }))}
          placeholder={`${t('dashboard.timesheet.submitwindow.approverfirstname')}`}
          sx={textFieldStyles(size.width > responsiveSizeTimesheet)}
        />
        <TextField
          value={data.surname}
          onChange={(e) => setData((prev) => ({ ...prev, surname: e.target.value }))}
          placeholder={`${t('dashboard.timesheet.submitwindow.approversurname')}`}
          sx={textFieldStyles(size.width > responsiveSizeTimesheet)}
        />
        <TextField
          value={data.email}
          onChange={(e) => setData((prev) => ({ ...prev, email: e.target.value }))}
          placeholder={`${t('dashboard.timesheet.submitwindow.approveremail')}`}
          sx={textFieldStyles(size.width > responsiveSizeTimesheet)}
        />
      </div>
      <MainButton
        disabled={!data.email || !data.email.match(emailRegExp || !data.firstName || !data.surname)}
        variant={
          !data.email || !data.email.match(emailRegExp) || !data.firstName || !data.surname ? 'disabled' : 'primary'
        }
        rounded
        forceWidth={size.width > responsiveSizeTimesheet ? '242px' : '100%'}
        action={requestNewApprover}
      >
        {!pending ? t('common.general.add') : <CircularProgress size={20} sx={{ color: colors.WHITE }} />}
      </MainButton>
    </>
  );
};

export const SubmittedSuccessfull = ({ afterRequestNewApprover }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const size = useWindowSize();
  const ctx = useContext(PageContext);

  return (
    <>
      {afterRequestNewApprover ? (
        <div className={classes.newApproverWrapper}>
          <p>
            {ctx.pageType === generalEnums.TIMESHEET
              ? t('dashboard.timesheet.submitwindow.requestnewsuccesslabel')
              : t('dashboard.expenses.submitwindow.requestnewsuccesslabel')}
          </p>
          <p>
            {ctx.pageType === generalEnums.TIMESHEET
              ? t('dashboard.timesheet.submitwindow.requestnewsuccesstext')
              : t('dashboard.expenses.submitwindow.requestnewsuccesstext')}
          </p>
        </div>
      ) : (
        <p className={`${classes.mainLabel} ${classes.anotherWidth}`}>
          {ctx.pageType === generalEnums.TIMESHEET
            ? t('dashboard.timesheet.submitwindow.submitted')
            : t('dashboard.expenses.submitwindow.submitted')}
        </p>
      )}
      <div className={classes.actionsContainer}>
        <MainButton
          rounded
          variant={buttonVariants.PRIMARY}
          forceWidth={size.width > responsiveSizeTimesheet ? '242px' : '100%'}
          action={() =>
            navigate(`/dashboard/create-${ctx.pageType === generalEnums.TIMESHEET ? 'timesheet' : 'claim'}`)
          }
        >
          {ctx.pageType === generalEnums.TIMESHEET
            ? t('dashboard.timesheets.newbuttontxt')
            : t('dashboard.expenses.newbuttontxt')}
        </MainButton>
        <MainButton
          rounded
          variant={buttonVariants.SECONDARY}
          forceWidth={size.width > responsiveSizeTimesheet ? '242px' : '100%'}
          action={() => navigate(`/dashboard/${ctx.pageType === generalEnums.TIMESHEET ? 'timesheets' : 'expenses'}`)}
        >
          {t('dashboard.timesheet.gotodashboard')}
        </MainButton>
      </div>
    </>
  );
};

const SubmitModal = ({ submitHandler, approvers, Id, stepType }) => {
  const [step, setStep] = useState(stepType || 'chooseApprover');
  const [submitting, setSubmitting] = useState(false);

  const submit = (approver) => {
    setSubmitting(true);
    submitHandler(approver, () => {
      setSubmitting(false);
      setStep('submittedSuccessfully');
    });
  };

  const steps = {
    chooseApprover: (
      <ChooseApprover
        submit={submit}
        changeStep={() => setStep('requestNewApprover')}
        approvers={approvers}
        submitting={submitting}
      />
    ),
    requestNewApprover: <RequestNewApprover changeStep={() => setStep('submittedSuccessfullyNew')} Id={Id} />,
    submittedSuccessfully: <SubmittedSuccessfull afterRequestNewApprover={false} />,
    submittedSuccessfullyNew: <SubmittedSuccessfull afterRequestNewApprover />
  };

  return <div className={classes.container}>{steps[step]}</div>;
};

export default SubmitModal;
